/**
 * Location master type helpers (NXTGEN-7862).
 * Consolidated locations may use any Chart of Accounts GL, not only GLs
 * mapped to that location id.
 */

import { LOCATION_MASTER_TYPE } from './constants.js';

function locationRecordId(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    if (value.$oid) return String(value.$oid).trim();
    if (typeof value.toHexString === 'function') return value.toHexString();
    const nested = value._id ?? value.id ?? value.location_id;
    if (nested != null && nested !== value) return locationRecordId(nested);
    return '';
  }
  return String(value).trim();
}

function locationTypeValue(type) {
  if (type == null || type === '') return '';
  if (typeof type === 'object') {
    return String(type.value || type.name || type.type || '').trim();
  }
  return String(type).trim();
}

/**
 * Case-insensitive compare against location master type Consolidated.
 * @param {unknown} type
 * @returns {boolean}
 */
export function isConsolidatedLocationType(type) {
  const normalized = locationTypeValue(type).toLowerCase();
  const consolidated = String(LOCATION_MASTER_TYPE.CONSOLIDATED || '')
    .trim()
    .toLowerCase();
  return normalized.length > 0 && normalized === consolidated;
}

/**
 * @param {Array<{_id?: unknown, id?: unknown, location_id?: unknown, type?: unknown}>} [locations]
 * @returns {Record<string, string>}
 */
export function buildLocationTypeById(locations = []) {
  const map = {};
  (Array.isArray(locations) ? locations : []).forEach((row) => {
    const id = locationRecordId(row);
    if (!id) return;
    const type = locationTypeValue(row?.type);
    if (!type && map[id]) return;
    map[id] = type;
  });
  return map;
}

/**
 * @param {unknown} locationId
 * @param {Record<string, string>} [locationTypeById]
 * @returns {boolean}
 */
export function isConsolidatedLocationId(locationId, locationTypeById = {}) {
  const id = locationRecordId(locationId);
  if (!id) return false;
  return isConsolidatedLocationType(locationTypeById[id]);
}

/**
 * @param {unknown[]} [locationIds]
 * @param {object[]} [locations]
 * @returns {{ hasConsolidated: boolean, hasNonConsolidated: boolean }}
 */
export function selectedLocationTypeFlags(locationIds = [], locations = []) {
  const typeById = buildLocationTypeById(locations);
  let hasConsolidated = false;
  let hasNonConsolidated = false;
  (Array.isArray(locationIds) ? locationIds : []).forEach((raw) => {
    const id = locationRecordId(raw);
    if (!id) return;
    if (isConsolidatedLocationId(id, typeById)) hasConsolidated = true;
    else hasNonConsolidated = true;
  });
  return { hasConsolidated, hasNonConsolidated };
}

/**
 * True when one assignment selects both Consolidated and entity locations.
 * @param {unknown[]} [locationIds]
 * @param {object[]} [locations]
 * @returns {boolean}
 */
export function assignmentHasMixedLocationTypes(locationIds = [], locations = []) {
  const flags = selectedLocationTypeFlags(locationIds, locations);
  return flags.hasConsolidated && flags.hasNonConsolidated;
}

/**
 * True when every selected location is Consolidated.
 * @param {unknown[]} [locationIds]
 * @param {object[]} [locations]
 * @returns {boolean}
 */
export function isConsolidatedOnlyLocationSelection(locationIds = [], locations = []) {
  const flags = selectedLocationTypeFlags(locationIds, locations);
  return flags.hasConsolidated && !flags.hasNonConsolidated;
}

/**
 * Copy `type` onto rows that omit it, using a fuller location list (LOCATION_GET).
 * @param {object[]} [locations]
 * @param {object[]} [typedLocations]
 * @returns {object[]}
 */
export function mergeLocationTypes(locations = [], typedLocations = []) {
  const typeById = buildLocationTypeById(typedLocations);
  return (Array.isArray(locations) ? locations : []).map((loc) => {
    if (!loc || typeof loc !== 'object') return loc;
    if (locationTypeValue(loc.type)) return loc;
    const id = locationRecordId(loc);
    const type = id ? typeById[id] : '';
    if (!type) return loc;
    return { ...loc, type };
  });
}
